import React, { useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  CartesianGrid,
  Cell,
  Legend,
  Line,
  LineChart,
  Pie,
  PieChart,
  PolarAngleAxis,
  PolarGrid,
  PolarRadiusAxis,
  Radar,
  RadarChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { hideFirstSensor } from "../../redux/infoWindRisk";
import { useChartSeriesVisibility } from "../../hooks/useChartSeriesVisibility";
import {
  useSensorPlacementColorBar,
  useActiveWindAlarmScenario,
} from "../CustomHooks";
import { generalNumbersFormatter } from "../utility/charts";
import {
  buildDirectionalChartData,
  buildMultiSeriesWindRoseData,
  extractWindRoseSeries,
  filterWindRoseSeriesBySensorCount,
  formatCoordinate,
  getWindRoseSeriesColor,
  resolveOverallScore,
  resolvePrimaryWindRoseSeries,
  resolveSensorId,
} from "../utility/sensorPlacement";
import { generateColor, sensorPlacementScale } from "../utils";
import InfoBoxDashboardContainer from "./InfoBoxDashboard";
import "./InfoBoxDashboard.css";
import "./SensorPlacementInfoBox.css";

const SensorPlacementInfoBox = () => {
  const sensorPlacementSelector = useSelector(
    (state) => state.infoWindRisk.firstSensorPlacementIsShowing,
  );

  const wholeSensorData = useSelector(
    (state) => state.infoWindRisk.sensorPlacementData,
  );
  const sensorCount = useSelector(
    (state) => state.infoWindRisk.sensorPlacementCount || 0,
  );

  const windRoseSeries = filterWindRoseSeriesBySensorCount(
    wholeSensorData?.windRoseSeries || extractWindRoseSeries(wholeSensorData),
    sensorCount,
  );
  const primaryData = resolvePrimaryWindRoseSeries(wholeSensorData);

  const { windDirectionList, uzFactorList } =
    buildDirectionalChartData(primaryData);
  const { data: combinedWindRoseData, seriesNames } =
    buildMultiSeriesWindRoseData(windRoseSeries);

  const coordinates = wholeSensorData?.coordinates ?? {};
  const sensorId = resolveSensorId(wholeSensorData);
  const overallScore = resolveOverallScore(wholeSensorData);

  const dispatch = useDispatch();
  const captureFrameRef = useRef();

  const { anySensorLayerVisible, windAlarmSensorsVisible } =
    useSensorPlacementColorBar();
  const activeWindAlarmScenario = useActiveWindAlarmScenario();
  const scenarioName = windAlarmSensorsVisible ? activeWindAlarmScenario : null;
  const hasSensorData =
    !!wholeSensorData &&
    (Object.keys(windRoseSeries).length > 0 ||
      wholeSensorData.position != null ||
      wholeSensorData.id);

  return (
    anySensorLayerVisible &&
    sensorPlacementSelector &&
    hasSensorData && (
      <InfoBoxDashboardContainer
        handleClickOutside={() => dispatch(hideFirstSensor())}
        needsCapture
        captureProps={{
          targetElement: () => captureFrameRef?.current,
          wrapperStyle: {
            position: "absolute",
            top: 8,
            right: 12,
            zIndex: 2,
          },
          style: {
            marginTop: 0,
            marginLeft: 0,
          },
        }}
        style={{
          display: "block",
          position: "absolute",
          top: "48px",
          width: "1180px",
          height: "min(640px, calc(100vh - 64px))",
          maxHeight: "calc(100vh - 64px)",
          overflow: "hidden",
          padding: "30px 14px 10px",
          gridTemplateColumns: "unset",
          gridTemplateRows: "unset",
          gap: 0,
        }}
      >
        <div className="sp-info-grid" ref={captureFrameRef}>
          <div className="sp-info-cell sp-info-cell--score">
            <DonoughtSection
              title="Overall Score"
              color={generateColor(overallScore, 0, 1, sensorPlacementScale)}
              value={overallScore}
            />
          </div>
          <div className="sp-info-cell sp-info-cell--wind-dir">
            <ChartSection
              title="Wind direction distribution"
              data={windDirectionList}
            />
          </div>
          <div className="sp-info-cell sp-info-cell--meta">
            <SensorCoordinatesSection
              sensorId={sensorId}
              coordinates={coordinates}
              scenarioName={scenarioName}
            />
          </div>
          <div className="sp-info-cell sp-info-cell--rose">
            <CombinedRadarSection
              title="Average wind speed"
              data={combinedWindRoseData}
              seriesNames={seriesNames}
              resetKey={sensorId || ""}
            />
          </div>
          <div className="sp-info-cell sp-info-cell--uz">
            <UZFactorSection
              title="UZ factor deviation in different direction"
              data={uzFactorList}
            />
          </div>
        </div>
      </InfoBoxDashboardContainer>
    )
  );
};

const SensorCoordinatesSection = ({ sensorId, coordinates, scenarioName }) => {
  const global = coordinates?.global ?? {};
  const local = coordinates?.local ?? {};

  return (
    <div className="sensor-coordinates-section">
      <p className="chart-title">Sensor information</p>
      <ul className="sensor-coordinates-list">
        {scenarioName ? (
          <li>
            <strong>Scenario name</strong>
            <div>{scenarioName}</div>
          </li>
        ) : null}
        <li>
          <strong>Sensor ID</strong>
          <div>{sensorId || "—"}</div>
        </li>
        <li>
          <strong>Sensor Position</strong>
          <div className="coordinate-group">
            <u>Global coordinate system</u>
            <ul>
              <li>Latitude: {formatCoordinate(global.latitude, 8)}</li>
              <li>Longitude: {formatCoordinate(global.longitude, 8)}</li>
              <li>
                Height above Sealevel:{" "}
                {formatCoordinate(global.height_above_sealevel_m, 2)}
                {global.height_above_sealevel_m != null &&
                global.height_above_sealevel_m !== ""
                  ? " m"
                  : ""}
              </li>
            </ul>
          </div>
          <div className="coordinate-group">
            <u>Local coordinate system</u>
            <ul>
              <li>X: {formatCoordinate(local.x)}</li>
              <li>Y: {formatCoordinate(local.y)}</li>
              <li>Z: {formatCoordinate(local.z)}</li>
            </ul>
          </div>
        </li>
      </ul>
    </div>
  );
};

const ChartSection = ({ title, data }) => (
  <div className="sp-chart-block">
    <p className="chart-title">{title}</p>
    <div className="sp-chart-canvas">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={data}
          margin={{ top: 4, right: 12, bottom: 4, left: 0 }}
        >
          <XAxis
            dataKey="direction"
            allowDataOverflow
            type="number"
            tickCount={9}
            domain={[0, 360]}
          />
          <YAxis
            allowDataOverflow
            type="number"
            domain={[-90, 450]}
            ticks={[-90, 0, 90, 180, 270, 360, 450]}
          />
          <Tooltip formatter={generalNumbersFormatter} />
          <Line
            type="monotone"
            dataKey="Simulated"
            stroke="#e60000"
            fill="#990000"
            activeDot={{ r: 5 }}
          />
          <Line
            type="monotone"
            dataKey="Ideal"
            stroke="#0000ff"
            fill="#005ce6"
          />
          <CartesianGrid strokeDasharray="3 3" />
          <Legend verticalAlign="bottom" height={28} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  </div>
);

const CombinedRadarSection = ({
  title,
  data,
  seriesNames = [],
  resetKey = "",
}) => {
  const { hiddenSeries, visibleSeries, toggleSeries } =
    useChartSeriesVisibility(seriesNames, resetKey);

  const radiusMax = Math.max(
    1.5,
    ...data.flatMap((row) =>
      visibleSeries.map((name) => Number(row[name]) || 0),
    ),
    0.1,
  );

  // Long Scenario Specific labels wrap onto a second row — reserve space.
  const legendHeight = seriesNames.length > 2 ? 48 : 32;

  return (
    <div className="sp-chart-block sp-chart-block--radar">
      <p className="chart-title">{title}</p>
      <div className="sp-chart-canvas sp-chart-canvas--radar">
        <ResponsiveContainer width="100%" height="100%">
          <RadarChart data={data} outerRadius="78%">
            <PolarGrid />
            <PolarAngleAxis dataKey="direction" tick={{ fontSize: 11 }} />
            <PolarRadiusAxis
              domain={[0, radiusMax]}
              tickCount={7}
              tick={{ fontSize: 11, fontWeight: 500 }}
              angle={10}
            />
            <Tooltip formatter={generalNumbersFormatter} />
            {seriesNames.map((seriesName, index) => {
              const { stroke, fill } = getWindRoseSeriesColor(index);
              return (
                <Radar
                  key={seriesName}
                  name={seriesName}
                  dataKey={seriesName}
                  stroke={stroke}
                  fill={fill}
                  fillOpacity={visibleSeries.length > 1 ? 0.35 : 0.7}
                  hide={!!hiddenSeries[seriesName]}
                />
              );
            })}
            <Legend
              verticalAlign="bottom"
              height={legendHeight}
              wrapperStyle={{
                cursor: "pointer",
                paddingTop: 2,
                overflow: "visible",
              }}
              onClick={(entry) => {
                if (entry?.dataKey) toggleSeries(entry.dataKey);
              }}
              formatter={(value) => (
                <span
                  className={
                    hiddenSeries[value]
                      ? "sp-legend-item sp-legend-item--hidden"
                      : "sp-legend-item"
                  }
                >
                  {value}
                </span>
              )}
            />
          </RadarChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

const UZFactorSection = ({ title, data }) => (
  <div className="sp-chart-block">
    <p className="chart-title">{title}</p>
    <div className="sp-chart-canvas">
      <ResponsiveContainer width="100%" height="100%">
        <LineChart
          data={data}
          margin={{ top: 4, right: 12, bottom: 4, left: 0 }}
        >
          <XAxis dataKey="direction" />
          <YAxis type="number" domain={[0, 1]} tickCount={6} />
          <Tooltip formatter={generalNumbersFormatter} />
          <Line
            type="monotone"
            dataKey="uzValue"
            stroke="#0000ff"
            fill="#005ce6"
            activeDot={{ r: 5 }}
          />
          <CartesianGrid strokeDasharray="3 3" />
          <Legend verticalAlign="bottom" height={28} />
        </LineChart>
      </ResponsiveContainer>
    </div>
  </div>
);

const renderCustomizedLabel = ({ cx, cy, color, value }) => {
  return (
    <text
      x={cx}
      y={cy}
      fill={color}
      textAnchor="middle"
      dominantBaseline="middle"
    >
      {(100 * value).toFixed(1)}%
    </text>
  );
};

const DonoughtSection = ({ title, color, value }) => {
  const safeValue = Number.isFinite(Number(value))
    ? Math.min(1, Math.max(0, Number(value)))
    : 0;
  const data = [
    { name: "Quality", value: safeValue, color },
    { name: "Rest", value: 1 - safeValue, color: "#fff" },
  ];

  return (
    <div className="sp-chart-block sp-chart-block--donut">
      <p className="chart-title">{title}</p>
      <div className="sp-chart-canvas sp-chart-canvas--donut">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie
              dataKey="value"
              label={(props) =>
                renderCustomizedLabel({ ...props, color, value: safeValue })
              }
              labelLine={false}
              startAngle={360}
              endAngle={0}
              data={data}
              cx="50%"
              cy="50%"
              innerRadius="48%"
              outerRadius="78%"
              stroke="none"
            >
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} stroke="#777" />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default SensorPlacementInfoBox;
